import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import DialogActions from '@mui/joy/DialogActions';
import Box from '@mui/joy/Box';
import { Button } from '@mui/joy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton as MuiIconButton } from '@mui/material';
import Skeleton from '@mui/joy/Skeleton';
import Typography from '@mui/joy/Typography';

import { useUser } from '../../../config/UserContext';
import { useUserCourse } from '../../../config/UserCourseContext';
import { injectKeyframes, CARD_STATES, SUBMISSION_PHASES } from './constants';
import { backdropSx, dialogSx, scrollbarSx } from './styles';
import CourseVersionCard from './CourseVersionCard';
import ExpandedCardView from './ExpandedCardView';

import { useCourseVersions, useCardStates, useSubmissionFlow } from './hooks';

import ErrorPopUp from '../../Utility/ErrorPopUp';
import UserConsentDialog from '../../Utility/UserConsentDialog';
import CartCoursesError from '../CartCoursesError';


export default function CourseVersionDetails(props) {
    const { open, data: courseData, onClose } = props;
    const crsId = courseData?.crsId;

    const { trainingProviders, userData } = useUser();
    const { cartCourseList } = useUserCourse();

    const {
        courseList,
        submittedVersions,
        loading: versionsLoading,
        error: versionsError,
        markSubmitted,
    } = useCourseVersions(crsId, userData?.user_id);

    const {
        expandedVersionId,
        initStates,
        getCardMeta,
        setCardMeta,
        expandView,
        collapseView,
    } = useCardStates();

    const {
        submissionPhase,
        errorMessage,
        setPendingByVersion,
        createCourseObj,
        executeFlow,
        continueAfterWarning,
        handleConsentResult,
        resetFlow,
    } = useSubmissionFlow({
        userId: userData?.user_id,
    });

    // ── Derived UI state ──
    const [showErrorPopUp, setShowErrorPopUp] = useState(false);
    const [errorPopUpMsg, setErrorPopUpMsg] = useState('');
    const [showCartError, setShowCartError] = useState(false);
    const [cartErrorList, setCartErrorList] = useState([]);
    const [cartErrorMsg, setCartErrorMsg] = useState('');
    const [showConsent, setShowConsent] = useState(false);
    const [consentCourseObj, setConsentCourseObj] = useState(null);
    const [flowPendingObj, setFlowPendingObj] = useState(null);
    const [flowPendingWarningType, setFlowPendingWarningType] = useState(null);

    const { addEnrolledCourse } = useUserCourse();

    const stackRef = useRef(null);

    // Inject global keyframes
    useEffect(() => { injectKeyframes(); }, []);

    // Initialize card states when course list loads
    useEffect(() => {
        if (courseList.length > 0) {
            initStates(courseList.map(i => i.crsVersionId));
        }
    }, [courseList, initStates]);

    // ── Scroll helper ──
    const scrollRight = useCallback((amount = 500) => {
        if (stackRef.current) {
            stackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
        }
    }, []);

    // ── Handle card expanded/open ──
    const handleOpenExpanded = useCallback((versionId) => {
        const item = courseList.find(
            i => (i.crsVersionId ?? `${i.crsId}_${i.versionNo}`) === versionId
        );
        if (!item) return;

        expandView(versionId);
        resetFlow();

        // Check if already submitted in this session
        if (submittedVersions[versionId]) {
            setCardMeta(versionId, {
                state: CARD_STATES.SUCCESS,
                course_url: submittedVersions[versionId]?.course_url || null,
            });
            return;
        }

        // Check if already in cart
        const inCart = cartCourseList?.pending?.some(
            el => el.courseVersionId === versionId
        );
        if (inCart) {
            setCardMeta(versionId, { state: CARD_STATES.SUCCESS });
            return;
        }
    }, [courseList, expandView, resetFlow, submittedVersions, setCardMeta, cartCourseList]);

    // ── Submit + Enroll: expand the card AND immediately start enrollment ──
    const handleSubmitAndEnroll = useCallback((versionId) => {

        const item = courseList.find(
            i => (i.crsVersionId ?? `${i.crsId}_${i.versionNo}`) === versionId
        );
        if (!item) return;

        // Expand view
        expandView(versionId);
        resetFlow();

        // Immediately trigger submission
        const courseObj = createCourseObj(item);
        setPendingByVersion(prev => ({ ...prev, [versionId]: courseObj }));
        setCardMeta(versionId, { state: CARD_STATES.LOADING });

        executeFlow(courseObj, {
            avoidWarning: false,
            avoidTPCheck: false,
        }).then(result => {
            if (result.success) {
                markSubmitted(versionId, result.course_url);
                setCardMeta(versionId, {
                    state: CARD_STATES.SUCCESS,
                    course_url: result.course_url || null,
                });
                setFlowPendingObj(null);
                setFlowPendingWarningType(null);
            } else if (result.warningType === 'warning14days') {
                setCardMeta(versionId, { state: CARD_STATES.IDLE });
                setFlowPendingObj(result.courseObj);
                setFlowPendingWarningType('warning14days');
            } else if (result.warningType === 'trainingProviderError') {
                setCardMeta(versionId, { state: CARD_STATES.IDLE });
                setConsentCourseObj(result.courseObj);
                setShowConsent(true);
            } else if (result.errorMessage) {
                setCardMeta(versionId, {
                    state: CARD_STATES.ERROR,
                    errorMessage: result.errorMessage,
                });
            }
        });
    }, [courseList, expandView, resetFlow, createCourseObj, executeFlow, markSubmitted, setCardMeta, setPendingByVersion]);

    // ── Launch training URL ──
    const handleWarningProceed = useCallback(async () => {
        if (!flowPendingObj) return;
        setFlowPendingWarningType(null);
        setCardMeta(expandedVersionId, { state: CARD_STATES.LOADING });

        const result = await continueAfterWarning(flowPendingObj, 'warning14days');

        if (result.success) {
            markSubmitted(expandedVersionId, result.course_url);
            setCardMeta(expandedVersionId, { state: CARD_STATES.SUCCESS, course_url: result.course_url || null });
            setFlowPendingObj(null);
        } else if (result.errorMessage && !result.warningType) {
            setCardMeta(expandedVersionId, { state: CARD_STATES.ERROR, errorMessage: result.errorMessage });
            setFlowPendingObj(null);
        }
    }, [flowPendingObj, expandedVersionId, continueAfterWarning, markSubmitted, setCardMeta]);

    const handleWarningDiscard = useCallback(() => {
        setFlowPendingObj(null);
        setFlowPendingWarningType(null);
        resetFlow();
    }, [resetFlow]);

    // ── Handle TP consent callback ──
    const handleConsentCallback = useCallback(async (agreed) => {
        setShowConsent(false);
        if (agreed && consentCourseObj) {
            setCardMeta(expandedVersionId, { state: CARD_STATES.LOADING });
            const result = await handleConsentResult(agreed, consentCourseObj);

            if (result.success) {
                markSubmitted(expandedVersionId, result.course_url);
                setCardMeta(expandedVersionId, { state: CARD_STATES.SUCCESS, course_url: result.course_url || null });
            } else if (result.errorMessage) {
                setCardMeta(expandedVersionId, { state: CARD_STATES.ERROR, errorMessage: result.errorMessage });
            }
            setConsentCourseObj(null);
        } else {
            setConsentCourseObj(null);
        }
    }, [consentCourseObj, expandedVersionId, handleConsentResult, markSubmitted, setCardMeta]);

    // ── Back to grid ──
    const handleBack = useCallback(() => {
        collapseView();
        resetFlow();
        setFlowPendingObj(null);
        setFlowPendingWarningType(null);
    }, [collapseView, resetFlow]);

    // ── Dismiss from error/success ──
    const handleDismiss = useCallback(() => {
        collapseView();
        resetFlow();
        setFlowPendingObj(null);
        setFlowPendingWarningType(null);
        //onClose();
    }, [collapseView, resetFlow]);

    // ── Dismiss from success (also closes the dialog) ──
    const handleSuccessDismiss = useCallback(() => {
        collapseView();
        resetFlow();
        setFlowPendingObj(null);
        setFlowPendingWarningType(null);
        onClose();
    }, [collapseView, resetFlow, onClose]);

    // ── Re-expand already-submitted card ──
    const handleReExpand = useCallback((versionId) => {
        setCardMeta(versionId, { state: CARD_STATES.SUCCESS, course_url: submittedVersions[versionId]?.course_url || null });
        expandView(versionId);
        resetFlow();
    }, [submittedVersions, expandView, resetFlow, setCardMeta]);

    // ── Launch training URL ──
    const handleLaunchTraining = useCallback((url) => {
        if (url) window.open(url, '_blank');
    }, []);

    // Current expanded item
    const expandedItem = expandedVersionId
        ? courseList.find(i => (i.crsVersionId ?? `${i.crsId}_${i.versionNo}`) === expandedVersionId)
        : null;

    const expandedCardMeta = expandedVersionId ? getCardMeta(expandedVersionId) : null;

    const inWarningFlow = flowPendingWarningType === 'warning14days';
    const isSubmitting = expandedCardMeta?.state === CARD_STATES.LOADING
        || submissionPhase === SUBMISSION_PHASES.PROCESSING
        || submissionPhase === SUBMISSION_PHASES.VALIDATING
        || submissionPhase === SUBMISSION_PHASES.SUBMITTING;

    return (
        <>
            <Modal
                keepMounted
                open={open}
                onClose={onClose}
                slotProps={{ backdrop: { sx: backdropSx(open) } }}
                sx={{ visibility: open ? 'visible' : 'hidden' }}
                disableScrollLock={true}
            >
                <ModalDialog sx={dialogSx(courseList.length, !!expandedVersionId)}>
                    <DialogTitle>
                        {expandedVersionId && CARD_STATES.SUCCESS !== expandedCardMeta?.state ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                    size="sm"
                                    variant="soft"
                                    color="neutral"
                                    startDecorator={<ChevronRightIcon sx={{ transform: 'rotate(180deg)' }} />}
                                    onClick={handleBack}
                                    sx={{ borderRadius: '20px', px: 2 }}
                                >
                                    Back to all versions
                                </Button>
                            </Box>
                        ) : (
                            `${courseList[0]?.crsId || ''} — Versions`
                        )}
                    </DialogTitle>

                    <DialogContent sx={{
                        ...scrollbarSx,
                        position: 'relative',
                        minHeight: 400,
                    }}>
                        {/* ── LOADING STATE ── */}
                        {versionsLoading && courseList.length === 0 && (
                            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Skeleton variant="text" level="h4" width="40%" />
                                <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 'sm' }} />
                            </Box>
                        )}

                        {/* ── ERROR STATE ── */}
                        {versionsError && courseList.length === 0 && (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography level="body-md" color="danger">
                                    {versionsError}
                                </Typography>
                                <Button variant="soft" color="neutral" onClick={onClose} sx={{ mt: 2 }}>
                                    Close
                                </Button>
                            </Box>
                        )}

                        {/* ── GRID + EXPANDED OVERLAY ── */}
                        {courseList.length > 0 && (
                            <Box sx={{ position: 'relative', minHeight: 400 }}>
                                {/* Scrollable Card Grid */}
                                <Box sx={{
                                    opacity: expandedVersionId ? 0 : 1,
                                    transition: 'opacity 300ms ease, filter 300ms ease',
                                    filter: expandedVersionId ? 'blur(2px)' : 'none',
                                    pointerEvents: expandedVersionId ? 'none' : 'auto',
                                    userSelect: expandedVersionId ? 'none' : 'auto',
                                    height: expandedVersionId ? 0 : 'auto',
                                    overflow: expandedVersionId ? 'hidden' : 'visible',
                                }}>
                                    <div ref={stackRef} style={{
                                        display: 'flex', flexDirection: 'row', gap: '12px',
                                        alignItems: 'flex-start', width: 'max-content', padding: '8px 4px',
                                    }}>
                                        {courseList.map((item) => {
                                            const versionId = item.crsVersionId ?? `${item.crsId}_${item.versionNo}`;
                                            const cardMeta = getCardMeta(versionId);
                                            const wasSubmitted = !!submittedVersions[versionId];
                                            const alreadyAddedFlag = cartCourseList?.pending?.length > 0
                                                ? cartCourseList.pending.some(el => el.courseVersionId === item.crsVersionId)
                                                : false;
                                            const isProcessing = cardMeta?.state === CARD_STATES.LOADING
                                                || submissionPhase === SUBMISSION_PHASES.PROCESSING
                                                || submissionPhase === SUBMISSION_PHASES.VALIDATING
                                                || submissionPhase === SUBMISSION_PHASES.SUBMITTING;

                                            return (
                                                <CourseVersionCard
                                                    key={versionId}
                                                    item={item}
                                                    cardMeta={cardMeta}
                                                    wasSubmitted={wasSubmitted}
                                                    alreadyAddedFlag={alreadyAddedFlag}
                                                    trainingProviders={trainingProviders}
                                                    isProcessing={isProcessing}
                                                    isExpanded={expandedVersionId === versionId}
                                                    onOpenExpanded={handleOpenExpanded}
                                                    onSubmitAndEnroll={handleSubmitAndEnroll}
                                                    onLaunchTraining={handleLaunchTraining}
                                                    onReExpand={handleReExpand}
                                                />
                                            );
                                        })}
                                    </div>

                                    {/* Scroll right button */}
                                    {courseList.length > 3 && !expandedVersionId && (
                                        <MuiIconButton size="medium" onClick={() => scrollRight(470)} aria-label="scroll-right"
                                            sx={{
                                                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                                zIndex: 10, bgcolor: 'background.surface', boxShadow: 1,
                                                '&:hover': { bgcolor: 'background.surface' },
                                            }}>
                                            <ChevronRightIcon />
                                        </MuiIconButton>
                                    )}
                                </Box>

                                {/* ── Expanded Card Overlay ── */}
                                {expandedVersionId && expandedItem && (
                                    <Box sx={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        zIndex: 20,
                                        animation: 'cvdScaleIn 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        transformOrigin: 'center center',
                                        bgcolor: 'background.surface',
                                        borderRadius: 'md',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}>
                                        {inWarningFlow ? (
                                            /* ── Inline 14-day warning ── */
                                            <Box sx={{
                                                display: 'flex', flexDirection: 'column',
                                                alignItems: 'center', justifyContent: 'center',
                                                height: '100%', gap: 2,
                                            }}>
                                                <ExpandedCardView
                                                    item={expandedItem}
                                                    submissionPhase={
                                                        expandedCardMeta?.state === CARD_STATES.SUCCESS
                                                            ? SUBMISSION_PHASES.SUCCESS
                                                            : expandedCardMeta?.state === CARD_STATES.ERROR
                                                                ? SUBMISSION_PHASES.ERROR
                                                                : submissionPhase
                                                    }
                                                    errorMessage={expandedCardMeta?.errorMessage || errorMessage}
                                                    course_url={
                                                        expandedCardMeta?.course_url
                                                        || submittedVersions[expandedVersionId]?.course_url
                                                        || null
                                                    }
                                                    trainingProviders={trainingProviders}
                                                    showWarning={true}
                                                    onWarningProceed={handleWarningProceed}
                                                    onWarningDiscard={handleWarningDiscard}
                                                    onDismiss={handleDismiss}
                                                    onSuccessDismiss={handleSuccessDismiss}
                                                />
                                            </Box>
                                        ) : (
                                            /* ── Expanded card view with progress ── */
                                            <ExpandedCardView
                                                item={expandedItem}
                                                submissionPhase={
                                                    expandedCardMeta?.state === CARD_STATES.SUCCESS
                                                        ? SUBMISSION_PHASES.SUCCESS
                                                        : expandedCardMeta?.state === CARD_STATES.ERROR
                                                            ? SUBMISSION_PHASES.ERROR
                                                            : submissionPhase
                                                }
                                                errorMessage={expandedCardMeta?.errorMessage || errorMessage}
                                                course_url={
                                                    expandedCardMeta?.course_url
                                                    || submittedVersions[expandedVersionId]?.course_url
                                                    || null
                                                }
                                                trainingProviders={trainingProviders}
                                                showWarning={false}
                                                onWarningProceed={handleWarningProceed}
                                                onWarningDiscard={handleWarningDiscard}
                                                onDismiss={handleDismiss}
                                                onSuccessDismiss={handleSuccessDismiss}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>

                    {!expandedVersionId && (
                        <DialogActions>
                            <Button variant="plain" color="neutral" onClick={onClose}>
                                Dismiss
                            </Button>
                        </DialogActions>
                    )}
                </ModalDialog>
            </Modal>

            {/* ── TP Consent Modal ── */}
            <UserConsentDialog
                show={showConsent}
                handleConsentCallback={handleConsentCallback}
                onHide={() => { setShowConsent(false); setConsentCourseObj(null); }}
            />

            {/* ── Generic Error Popup ── */}
            <ErrorPopUp show={showErrorPopUp} data={errorPopUpMsg} onHide={() => setShowErrorPopUp(false)} />

            {/* ── Cart Equivalent Course Error ── */}
            <CartCoursesError
                open={showCartError}
                onClose={() => setShowCartError(false)}
                cartErrorBoxMsg={cartErrorMsg}
                cartErrorBoxList={cartErrorList}
                onRemoveFromCart={() => { /* TODO: implement replace_submission */ }}
            />
        </>
    );
}